import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth-context";
import { useActiveLocations } from "@/hooks/use-active-locations";
import { useAiEnabled } from "@/hooks/use-ai-enabled";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sparkles, RefreshCw, Clock, FileText, ChevronRight, ChevronDown, AlertCircle, CheckCircle2, Database, Plus, Pencil, Trash2, Copy, RotateCcw, X, Save, Eye, MoreVertical, Info, Filter, Server, Users, Lightbulb, Search, MapPin, Calendar, BookOpen, Pin, PinOff, Loader2, ChevronLeft, ArrowRight, ArrowUp, ArrowDown } from "lucide-react";
import { AnimalDetailDrawer } from "@/components/animals/AnimalDetailDrawer";
import { SearchEmptyState } from "@/components/ui/search-empty-state";
import { PageHeader } from "@/components/page-header";
import { format } from "date-fns";
import { normalizeSummaryForRender } from "@/lib/normalize-summary";
import { usePageTitle } from "@/hooks/use-page-title";
import { AppMarkdown } from "@/components/ui/app-markdown";

interface PromptTemplate {
  systemInstructions: string;
  styleGuidance: string;
  formatConstraints: string;
  lengthBounds: { min: number; max: number };
}

interface PromptDefinition {
  id: string;
  name: string;
  intent: string;
  scope: string;
  resolver: string;
  datasets: string[];
  model: string;
  enabled: boolean;
  visibility: "admin" | "staff";
  isBuiltIn: boolean;
  isBriefingTemplate?: boolean;
  template?: PromptTemplate;
  createdAt: string;
  updatedAt?: string;
}

interface SummaryEvidence {
  selectionMethod: {
    description: string;
    filters?: Array<{ label: string; value: string }>;
  };
  dataSources: Array<{
    name: string;
    description: string;
    recordCount: number;
    dateRange?: { earliest: string; latest: string };
    sampleFields?: string[];
  }>;
  processing: {
    animalsIncluded: number;
    dataPointsAnalyzed: number;
    timeframe?: string;
    notes?: string[];
  };
}

interface GeneratedSummary {
  id: string;
  promptDefinitionId: string;
  scope: string;
  resolvedIds: string[];
  contentJson: {
    interactionCount?: number;
    bullets: string[];
    trendNarrative: string;
    additionalSections?: Record<string, string>;
  } | null;
  auditJson: {
    generatedAt: string;
    model: string;
    promptVersion: string;
    tokenUsage?: { prompt: number; completion: number };
  } | null;
  evidenceJson?: SummaryEvidence | null;
  datasetsUsed: string[];
  status: "fresh" | "stale" | "generating" | "error";
  generatedAt: string;
  promptName?: string;
  promptIntent?: string;
}

interface AllowLists {
  scopes: readonly string[];
  resolvers: readonly string[];
  datasets: readonly string[];
  models: readonly string[];
}

// Gate Q: Briefings types
interface BriefingStructuredSection {
  key: string;
  title: string;
  contentJson: Record<string, unknown> | null;
  datasetsUsed: string[];
  generatedAt: string;
  error?: boolean;
  errorMessage?: string;
}

interface BriefingListItem {
  id: string;
  promptDefinitionId: string;
  locationCode: string;
  species: string;
  referenceDate: string;
  title: string;
  status: "generating" | "ready" | "partial" | "failed" | "stale";
  isPinned: boolean;
  errorMessage?: string | null;
  promptName?: string | null;
  generatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BriefingDetail extends BriefingListItem {
  content?: string | null;
  contentStructured?: BriefingStructuredSection[] | null;
  narrativeScript?: string | null;
}

// A-4: Renders structured JSON content from briefing sections
function GenericJsonRenderer({ data }: { data: Record<string, unknown> | null }) {
  if (!data) return <p className="text-sm text-muted-foreground italic">No content available.</p>;

  function renderValue(value: unknown, depth: number = 0): JSX.Element {
    if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;
    if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;
    if (typeof value === "number" || typeof value === "string") return <span>{String(value)}</span>;
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="text-muted-foreground italic">None</span>;
      return (
        <ol className={`list-decimal list-inside space-y-1${depth > 0 ? " mt-1" : ""}`}>
          {value.map((item, i) => (
            <li key={i} className="text-sm leading-relaxed">
              {typeof item === "object" && item !== null
                ? <div className="ml-4 mt-0.5">{renderValue(item, depth + 1)}</div>
                : renderValue(item, depth + 1)}
            </li>
          ))}
        </ol>
      );
    }
    if (typeof value === "object") {
      const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== null && v !== undefined);
      if (entries.length === 0) return <span className="text-muted-foreground italic">—</span>;
      return (
        <div className={`space-y-0.5${depth > 0 ? " mt-1 pl-3 border-l border-border" : ""}`}>
          {entries.map(([k, v]) => (
            <div key={k} className="flex flex-wrap gap-1 text-sm">
              <span className="font-medium text-foreground capitalize">{k.replace(/([A-Z])/g, " $1")}:</span>
              <span>{renderValue(v, depth + 1)}</span>
            </div>
          ))}
        </div>
      );
    }
    return <span>{String(value)}</span>;
  }

  return <div className="text-sm">{renderValue(data)}</div>;
}

interface BriefingSection {
  key: string;
  title: string;
  order: number;
  enabled: boolean;
  datasets: string[];
  systemInstructions: string;
  formatConstraints?: string;
  lookbackDays?: number;
  overdueAnimalCap?: number;
}

interface BriefingTemplate {
  id: string;
  name: string;
  intent: string;
  model: string;
  enabled: boolean;
  isBuiltIn: boolean;
  updatedAt: string;
  template?: { systemInstructions?: string; styleGuidance?: string; formatConstraints?: string; lengthBounds?: { min: number; max: number } };
  templateConfig?: { sections: BriefingSection[]; narrativeInstructions?: string };
  templateConfigVersion?: number;
}

function speciesLabel(s: string): string {
  return s === "DOG" ? "Dogs" : s === "CAT" ? "Cats" : s;
}

const SCOPE_RESOLVER_MAP: Record<string, string[]> = {
  single_animal: ["by_animal_id"],
  location: ["by_location_code"],
  filtered_group: ["by_explicit_animal_ids", "by_animals_page_filters", "by_interactions_page_filters"],
  all_animals: ["by_animals_page_filters"],
};

const DEFAULT_TEMPLATE: PromptTemplate = {
  systemInstructions: "You are an expert animal behavior analyst helping shelter staff understand animal behavior patterns.",
  styleGuidance: "Analyze the following data and provide a concise summary.",
  formatConstraints: `Please provide:
1. A list of 3-5 bullet points summarizing key patterns
2. A brief trend narrative (1-2 sentences)

Format your response as JSON with the following structure:
{
  "bullets": ["bullet1", "bullet2", ...],
  "trendNarrative": "Brief trend description..."
}`,
  lengthBounds: { min: 100, max: 500 },
};

export default function Summaries() {
  usePageTitle("Summaries");
  const { user } = useAuth();
  const { toast } = useToast();
  const { isAiEnabled, isLoading: isAiLoading } = useAiEnabled();
  const [, setLocation] = useLocation();
  const [selectedTab, setSelectedTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("briefing") ? "briefings" : "summaries";
  });

  const openBriefing = useCallback((id: string) => {
    setSelectedBriefingId(id);
    setSelectedTab("briefings");
    setLocation(`/summaries?briefing=${id}`);
  }, [setLocation]);

  const closeBriefing = useCallback(() => {
    setSelectedBriefingId(null);
    setLocation("/summaries");
  }, [setLocation]);

  const handleTabChange = useCallback((tab: string) => {
    setSelectedTab(tab);
    if (tab !== "briefings") {
      setLocation("/summaries");
    }
  }, [setLocation]);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedPromptIdForGenerate, setSelectedPromptIdForGenerate] = useState<string | null>(null);
  const [locationCode, setLocationCode] = useState<string>("");

  // H5.5: Summaries tab filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<string>("all");
  const [selectedDays, setSelectedDays] = useState<string>("all");
  
  // H5.5: Active locations for filter dropdown
  const { locations: activeLocations, isLoading: isLocationsLoading } = useActiveLocations();

  // Sources expansion state (H5.2 Rev 1.7)
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  
  // Prompt drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<PromptDefinition | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [actionPrompt, setActionPrompt] = useState<PromptDefinition | null>(null);

  // Edit form state
  const [editForm, setEditForm] = useState<Partial<PromptDefinition & { template: PromptTemplate }>>({});

  // Gate Q: Briefings tab state
  const [briefingGenerateOpen, setBriefingGenerateOpen] = useState(false);
  // Gate Q1.5: Section editor state
  const [templateDrawerOpen, setTemplateDrawerOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<BriefingTemplate | null>(null);
  const [editingSectionKey, setEditingSectionKey] = useState<string | null>(null);
  const [sectionEditForm, setSectionEditForm] = useState<{
    systemInstructions: string;
    formatConstraints: string;
    lookbackDays: string;
    overdueAnimalCap: string;
    enabled: boolean;
  } | null>(null);
  const [sectionResetDialogOpen, setSectionResetDialogOpen] = useState(false);
  const [resetSectionKey, setResetSectionKey] = useState<string | null>(null);
  // Gate Q2 Phase 1 — UX-2 / LW-1: top-level template settings + narrative form
  const [templateTopLevelForm, setTemplateTopLevelForm] = useState<{
    model: string;
    systemInstructions: string;
    styleGuidance: string;
    narrativeInstructions: string;
  } | null>(null);
  const [narrativeResetLoading, setNarrativeResetLoading] = useState(false);
  const [briefingLocationCode, setBriefingLocationCode] = useState<string>("");
  const [briefingSpecies, setBriefingSpecies] = useState<string>("DOG");
  const [briefingDate, setBriefingDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [briefingTemplateId, setBriefingTemplateId] = useState<string>("");
  const [selectedBriefingId, setSelectedBriefingId] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("briefing");
  });
  const [animalDrawerOpen, setAnimalDrawerOpen] = useState(false);
  const [animalDrawerId, setAnimalDrawerId] = useState<string | null>(null);
  const [briefingLocationFilter, setBriefingLocationFilter] = useState<string>("all");

  const { data: briefingsData, isLoading: briefingsLoading } = useQuery<{
    briefings: BriefingListItem[];
    total: number;
  }>({
    queryKey: ["/api/agent/briefings", briefingLocationFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (briefingLocationFilter !== "all") params.set("locationCode", briefingLocationFilter);
      const res = await fetch(`/api/agent/briefings?${params}`);
      if (!res.ok) throw new Error("Failed to fetch briefings");
      return res.json();
    },
    refetchInterval: (query) => {
      const d = query.state.data as { briefings: BriefingListItem[] } | undefined;
      return d?.briefings?.some((b: BriefingListItem) => b.status === "generating") ? 2000 : false;
    },
    enabled: selectedTab === "briefings",
  });

  const { data: selectedBriefing, isLoading: selectedBriefingLoading } = useQuery<BriefingDetail>({
    queryKey: ["/api/agent/briefings", selectedBriefingId],
    queryFn: async () => {
      const res = await fetch(`/api/agent/briefings/${selectedBriefingId}`);
      if (!res.ok) throw new Error("Failed to fetch briefing");
      return res.json();
    },
    enabled: !!selectedBriefingId,
    refetchInterval: (query) => (query.state.data as BriefingDetail | undefined)?.status === "generating" ? 2000 : false,
  });

  const { data: briefingTemplatesData, isLoading: briefingTemplatesLoading } = useQuery<{
    templates: BriefingTemplate[];
  }>({
    queryKey: ["/api/agent/briefing-templates"],
    enabled: selectedTab === "briefings",
  });

  const generateBriefingMutation = useMutation({
    mutationFn: async (body: { promptDefinitionId: string; locationCode: string; species: string; referenceDate: string }) => {
      return apiRequest("POST", "/api/agent/briefings/generate", body);
    },
    onSuccess: (data: BriefingListItem) => {
      setBriefingGenerateOpen(false);
      openBriefing(data.id);
      queryClient.invalidateQueries({ queryKey: ["/api/agent/briefings"] });
    },
    onError: () => {
      toast({ title: "Generation failed", description: "Could not start briefing generation.", variant: "destructive" });
    },
  });

  const pinBriefingMutation = useMutation({
    mutationFn: async ({ id, isPinned }: { id: string; isPinned: boolean }) => {
      return apiRequest("POST", `/api/agent/briefings/${id}/pin`, { isPinned });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agent/briefings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/agent/briefings", selectedBriefingId] });
    },
  });

  const regenerateBriefingMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("POST", `/api/agent/briefings/${id}/regenerate`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agent/briefings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/agent/briefings", selectedBriefingId] });
    },
    onError: () => {
      toast({ title: "Regeneration failed", description: "Could not restart briefing generation.", variant: "destructive" });
    },
  });

  // Gate Q1.5: Section edit mutation
  const patchSectionMutation = useMutation({
    mutationFn: async ({ templateId, sectionKey, data }: {
      templateId: string;
      sectionKey: string;
      data: { systemInstructions?: string; formatConstraints?: string; lookbackDays?: number | null; overdueAnimalCap?: number | null; enabled?: boolean };
    }) => {
      return apiRequest("PATCH", `/api/summaries/prompts/${templateId}/sections/${sectionKey}`, data);
    },
    onSuccess: (response: any) => {
      toast({ title: "Section updated", description: "Prompt instructions saved." });
      queryClient.invalidateQueries({ queryKey: ["/api/agent/briefing-templates"] });
      setEditingSectionKey(null);
      setSectionEditForm(null);
      if (selectedTemplate) {
        setSelectedTemplate({
          ...selectedTemplate,
          templateConfig: response.prompt?.templateConfig,
          templateConfigVersion: response.prompt?.templateConfigVersion,
        });
      }
    },
    onError: () => {
      toast({ title: "Save failed", description: "Could not save section instructions.", variant: "destructive" });
    },
  });

  // Gate Q1.5: Section reset mutation
  const resetSectionMutation = useMutation({
    mutationFn: async ({ templateId, sectionKey }: { templateId: string; sectionKey: string }) => {
      return apiRequest("POST", `/api/summaries/prompts/${templateId}/sections/${sectionKey}/reset`);
    },
    onSuccess: (response: any) => {
      toast({ title: "Section reset", description: "Section restored to factory defaults." });
      queryClient.invalidateQueries({ queryKey: ["/api/agent/briefing-templates"] });
      setSectionResetDialogOpen(false);
      setResetSectionKey(null);
      if (selectedTemplate) {
        const updatedTemplate = {
          ...selectedTemplate,
          templateConfig: response.prompt?.templateConfig,
          templateConfigVersion: response.prompt?.templateConfigVersion,
        };
        setSelectedTemplate(updatedTemplate);
        if (editingSectionKey === response.section?.key && response.section) {
          setSectionEditForm({
            systemInstructions: response.section.systemInstructions ?? "",
            formatConstraints: response.section.formatConstraints ?? "",
            lookbackDays: response.section.lookbackDays?.toString() ?? "",
            overdueAnimalCap: response.section.overdueAnimalCap?.toString() ?? "",
            enabled: response.section.enabled ?? true,
          });
        }
      }
    },
    onError: () => {
      toast({ title: "Reset failed", description: "Could not reset section.", variant: "destructive" });
      setSectionResetDialogOpen(false);
      setResetSectionKey(null);
    },
  });

  // Gate Q2 Phase 1 — UX-2 / LW-1: update top-level template settings + narrativeInstructions + Layer 2
  const updateTemplateMutation = useMutation({
    mutationFn: async ({ templateId, data }: {
      templateId: string;
      data: {
        model?: string;
        template?: { systemInstructions?: string; styleGuidance?: string };
        templateConfig?: { sections: BriefingSection[]; narrativeInstructions: string };
      };
    }) => {
      return apiRequest("PATCH", `/api/summaries/prompts/${templateId}`, data);
    },
    onSuccess: (response: any) => {
      toast({ title: "Template updated", description: "Template settings saved." });
      queryClient.invalidateQueries({ queryKey: ["/api/agent/briefing-templates"] });
      if (selectedTemplate) {
        const updated = {
          ...selectedTemplate,
          model: response.prompt?.model ?? selectedTemplate.model,
          template: response.prompt?.template ?? selectedTemplate.template,
          templateConfig: response.prompt?.templateConfig ?? selectedTemplate.templateConfig,
          templateConfigVersion: response.prompt?.templateConfigVersion ?? selectedTemplate.templateConfigVersion,
        };
        setSelectedTemplate(updated);
        setTemplateTopLevelForm({
          model: updated.model,
          systemInstructions: updated.template?.systemInstructions ?? "",
          styleGuidance: updated.template?.styleGuidance ?? "",
          narrativeInstructions: updated.templateConfig?.narrativeInstructions ?? "",
        });
      }
    },
    onError: () => {
      toast({ title: "Save failed", description: "Could not save template settings.", variant: "destructive" });
    },
  });

  // Gate Q1.5: Section reorder mutation
  const reorderSectionMutation = useMutation({
    mutationFn: async ({ templateId, sectionKey, direction }: {
      templateId: string;
      sectionKey: string;
      direction: "up" | "down";
    }) => {
      return apiRequest("POST", `/api/summaries/prompts/${templateId}/sections/reorder`, { sectionKey, direction });
    },
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/agent/briefing-templates"] });
      if (selectedTemplate) {
        setSelectedTemplate({
          ...selectedTemplate,
          templateConfig: response.prompt?.templateConfig,
          templateConfigVersion: response.prompt?.templateConfigVersion,
        });
      }
    },
    onError: () => {
      toast({ title: "Reorder failed", description: "Could not move section.", variant: "destructive" });
    },
  });

  const { data: promptsData, isLoading: promptsLoading } = useQuery<{
    prompts: PromptDefinition[];
    allowLists: AllowLists;
  }>({
    queryKey: ["/api/summaries/prompts"],
  });

  // H5.5: Build summaries API URL with filter params
  const buildSummariesUrl = () => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) {
      params.set("search", searchQuery.trim());
    }
    if (selectedLocation !== "all") {
      params.set("location", selectedLocation);
    }
    if (selectedDays !== "all") {
      params.set("days", selectedDays);
    }
    const queryString = params.toString();
    return queryString ? `/api/summaries?${queryString}` : "/api/summaries";
  };

  const { data: summariesData, isLoading: summariesLoading } = useQuery<{
    summaries: GeneratedSummary[];
    pagination: { limit: number; offset: number; hasMore: boolean };
  }>({
    queryKey: ["/api/summaries", { search: searchQuery.trim() || undefined, location: selectedLocation !== "all" ? selectedLocation : undefined, days: selectedDays !== "all" ? selectedDays : undefined }],
    queryFn: async () => {
      const res = await fetch(buildSummariesUrl(), { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch summaries");
      return res.json();
    },
  });

  // Mutations
  const generateMutation = useMutation({
    mutationFn: async (data: { promptDefinitionId: string; resolverInput: Record<string, unknown> }) => {
      return apiRequest("POST", "/api/summaries/generate", data);
    },
    onSuccess: () => {
      toast({ title: "Summary Generated", description: "The AI summary has been generated successfully." });
      queryClient.invalidateQueries({ queryKey: ["/api/summaries"] });
      setGenerateDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({ title: "Generation Failed", description: error.message, variant: "destructive" });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: Partial<PromptDefinition>) => {
      return apiRequest("POST", "/api/summaries/prompts", data);
    },
    onSuccess: (response: { prompt: PromptDefinition }) => {
      toast({ title: "Prompt Created", description: "The prompt definition has been created." });
      queryClient.invalidateQueries({ queryKey: ["/api/summaries/prompts"] });
      setIsCreating(false);
      setIsEditing(false);
      setSelectedPrompt(response.prompt);
    },
    onError: (error: Error) => {
      setEditError(error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PromptDefinition> }) => {
      return apiRequest("PATCH", `/api/summaries/prompts/${id}`, data);
    },
    onSuccess: (response: { prompt: PromptDefinition }) => {
      toast({ title: "Prompt Updated", description: "The prompt definition has been updated." });
      queryClient.invalidateQueries({ queryKey: ["/api/summaries/prompts"] });
      setIsEditing(false);
      setSelectedPrompt(response.prompt);
    },
    onError: (error: Error) => {
      setEditError(error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/summaries/prompts/${id}`);
    },
    onSuccess: () => {
      toast({ title: "Prompt Deleted", description: "The prompt definition has been deleted." });
      queryClient.invalidateQueries({ queryKey: ["/api/summaries/prompts"] });
      if (drawerOpen) {
        setDrawerOpen(false);
        setSelectedPrompt(null);
      }
      setDeleteDialogOpen(false);
      setActionPrompt(null);
    },
    onError: (error: Error) => {
      toast({ title: "Delete Failed", description: error.message, variant: "destructive" });
      setDeleteDialogOpen(false);
      setActionPrompt(null);
    },
  });

  const resetMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("POST", `/api/summaries/prompts/${id}/reset`);
    },
    onSuccess: (response: { prompt: PromptDefinition }) => {
      toast({ title: "Prompt Reset", description: "The prompt has been reset to factory defaults." });
      queryClient.invalidateQueries({ queryKey: ["/api/summaries/prompts"] });
      if (selectedPrompt?.id === response.prompt.id) {
        setSelectedPrompt(response.prompt);
      }
      setResetDialogOpen(false);
      setActionPrompt(null);
    },
    onError: (error: Error) => {
      toast({ title: "Reset Failed", description: error.message, variant: "destructive" });
      setResetDialogOpen(false);
      setActionPrompt(null);
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("POST", `/api/summaries/prompts/${id}/duplicate`);
    },
    onSuccess: (response: { prompt: PromptDefinition }) => {
      toast({ title: "Prompt Duplicated", description: "A copy of the prompt has been created." });
      queryClient.invalidateQueries({ queryKey: ["/api/summaries/prompts"] });
      setSelectedPrompt(response.prompt);
    },
    onError: (error: Error) => {
      toast({ title: "Duplicate Failed", description: error.message, variant: "destructive" });
    },
  });

  // H5.2: AI Draft state
  const [aiDraftDrawerOpen, setAiDraftDrawerOpen] = useState(false);
  const [aiDraftTab, setAiDraftTab] = useState<"describe" | "review">("describe");
  const [aiDescription, setAiDescription] = useState("");
  const [aiDescriptionLocked, setAiDescriptionLocked] = useState(false);
  const [aiDraftResult, setAiDraftResult] = useState<{
    draft: Partial<PromptDefinition & { template: PromptTemplate }>;
    warnings: string[];
    confidence: number;
  } | null>(null);
  const [aiDraftError, setAiDraftError] = useState<string | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const fetchSuggestions = async (bustCache = false) => {
    setSuggestionsLoading(true);
    try {
      const url = bustCache 
        ? `/api/summaries/prompts/suggestions?_t=${Date.now()}`
        : "/api/summaries/prompts/suggestions";
      const response = await apiRequest("GET", url);
      const data = response as { suggestions: string[] };
      setAiSuggestions(data.suggestions || []);
    } catch {
      setAiSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const aiDraftMutation = useMutation({
    mutationFn: async (description: string) => {
      return apiRequest("POST", "/api/summaries/prompts/ai-draft", { description });
    },
    onSuccess: (response: { draft: AiDraftSuggestion; warnings: string[]; confidence: number }) => {
      setAiDraftResult({
        draft: {
          name: response.draft.name,
          intent: response.draft.intent,
          scope: response.draft.scope,
          resolver: response.draft.resolver,
          datasets: response.draft.datasets,
          model: response.draft.model,
          enabled: false,
          visibility: "admin",
          template: response.draft.template,
        },
        warnings: response.warnings,
        confidence: response.confidence,
      });
      setAiDraftError(null);
      setAiDescriptionLocked(true);
      setAiDraftTab("review");
    },
    onError: (error: Error) => {
      setAiDraftError(error.message);
      setAiDraftResult(null);
    },
  });

  interface AiDraftSuggestion {
    name: string;
    intent: string;
    scope: string;
    resolver: string;
    datasets: string[];
    model: string;
    template: PromptTemplate;
  }

  const handleAiDraftGenerate = () => {
    if (!aiDescription.trim()) return;
    setAiDraftError(null);
    aiDraftMutation.mutate(aiDescription);
  };

  const handleAiDraftSave = () => {
    if (!aiDraftResult?.draft) return;
    
    const payload = {
      ...aiDraftResult.draft,
      enabled: false,
      visibility: "admin" as const,
      template: aiDraftResult.draft.template ? {
        ...aiDraftResult.draft.template,
        lengthBounds: {
          min: typeof aiDraftResult.draft.template.lengthBounds?.min === 'string'
            ? parseInt(aiDraftResult.draft.template.lengthBounds.min, 10) || 100
            : aiDraftResult.draft.template.lengthBounds?.min ?? 100,
          max: typeof aiDraftResult.draft.template.lengthBounds?.max === 'string'
            ? parseInt(aiDraftResult.draft.template.lengthBounds.max, 10) || 500
            : aiDraftResult.draft.template.lengthBounds?.max ?? 500,
        },
      } : DEFAULT_TEMPLATE,
    };
    
    createMutation.mutate(payload, {
      onSuccess: () => {
        closeAiDraftDrawer();
      },
    });
  };

  const closeAiDraftDrawer = () => {
    setAiDraftDrawerOpen(false);
    setAiDescription("");
    setAiDescriptionLocked(false);
    setAiDraftResult(null);
    setAiDraftError(null);
    setAiDraftTab("describe");
  };

  const resetAiDraft = () => {
    setAiDescription("");
    setAiDescriptionLocked(false);
    setAiDraftResult(null);
    setAiDraftError(null);
    setAiDraftTab("describe");
  };

  // Initialize edit form when selecting a prompt or starting create
  useEffect(() => {
    if (isCreating) {
      const defaultScope = promptsData?.allowLists.scopes[0] || "single_animal";
      const defaultResolver = SCOPE_RESOLVER_MAP[defaultScope]?.[0] || "by_animal_id";
      setEditForm({
        name: "",
        intent: "",
        scope: defaultScope,
        resolver: defaultResolver,
        datasets: [promptsData?.allowLists.datasets[0] || "CANONICAL_INTERACTIONS"],
        model: "gpt-4o-mini",
        enabled: false,
        visibility: "admin",
        template: { ...DEFAULT_TEMPLATE },
      });
    } else if (selectedPrompt && isEditing) {
      setEditForm({
        name: selectedPrompt.name,
        intent: selectedPrompt.intent,
        scope: selectedPrompt.scope,
        resolver: selectedPrompt.resolver,
        datasets: [...selectedPrompt.datasets],
        model: selectedPrompt.model,
        enabled: selectedPrompt.enabled,
        visibility: selectedPrompt.visibility,
        template: selectedPrompt.template ? { ...selectedPrompt.template } : { ...DEFAULT_TEMPLATE },
      });
    }
    setEditError(null);
  }, [isCreating, isEditing, selectedPrompt, promptsData]);

  // Update resolver options when scope changes
  const handleScopeChange = (scope: string) => {
    const validResolvers = SCOPE_RESOLVER_MAP[scope] || [];
    setEditForm(prev => ({
      ...prev,
      scope,
      resolver: validResolvers[0] || prev.resolver,
    }));
  };

  const handleDatasetToggle = (dataset: string) => {
    setEditForm(prev => {
      const datasets = prev.datasets || [];
      if (datasets.includes(dataset)) {
        if (datasets.length > 1) {
          return { ...prev, datasets: datasets.filter(d => d !== dataset) };
        }
        return prev; // Must have at least one
      }
      return { ...prev, datasets: [...datasets, dataset] };
    });
  };

  const handleSave = () => {
    // Ensure template lengthBounds are numbers (input fields return strings)
    const payload = {
      ...editForm,
      template: editForm.template ? {
        ...editForm.template,
        lengthBounds: {
          min: typeof editForm.template.lengthBounds?.min === 'string' 
            ? parseInt(editForm.template.lengthBounds.min, 10) || 0 
            : editForm.template.lengthBounds?.min ?? 0,
          max: typeof editForm.template.lengthBounds?.max === 'string' 
            ? parseInt(editForm.template.lengthBounds.max, 10) || 500 
            : editForm.template.lengthBounds?.max ?? 500,
        },
      } : undefined,
    };

    if (isCreating) {
      createMutation.mutate(payload);
    } else if (selectedPrompt) {
      updateMutation.mutate({ id: selectedPrompt.id, data: payload });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsCreating(false);
    setEditError(null);
    if (isCreating) {
      setDrawerOpen(false);
    }
  };

  const openPromptDrawer = (prompt: PromptDefinition) => {
    setSelectedPrompt(prompt);
    setIsCreating(false);
    setIsEditing(false);
    setDrawerOpen(true);
  };

  const openNewPromptDrawer = () => {
    setSelectedPrompt(null);
    setIsCreating(true);
    setIsEditing(true);
    setDrawerOpen(true);
  };

  const handleGenerate = () => {
    if (!selectedPromptIdForGenerate) return;
    const prompt = promptsData?.prompts.find(p => p.id === selectedPromptIdForGenerate);
    if (!prompt) return;

    const resolverInput: Record<string, unknown> = {};
    if (prompt.resolver === "by_location_code") {
      resolverInput.locationCode = locationCode;
    }
    
    generateMutation.mutate({ promptDefinitionId: selectedPromptIdForGenerate, resolverInput });
  };

  const enabledPrompts = promptsData?.prompts.filter(p => p.enabled) || [];
  const groupPrompts = enabledPrompts.filter(p => p.scope !== "single_animal");

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "fresh":
        return <Badge variant="default" className="bg-green-500"><CheckCircle2 className="w-3 h-3 mr-1" />Fresh</Badge>;
      case "stale":
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Stale</Badge>;
      case "generating":
        return <Badge variant="outline"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Generating</Badge>;
      case "error":
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" />Error</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getScopeBadge = (scope: string) => {
    switch (scope) {
      case "single_animal":
        return <Badge variant="outline">Single Animal</Badge>;
      case "location":
        return <Badge variant="secondary">Location</Badge>;
      case "filtered_group":
        return <Badge variant="secondary">Filtered Group</Badge>;
      case "all_animals":
        return <Badge variant="secondary">All Animals</Badge>;
      default:
        return <Badge variant="outline">{scope}</Badge>;
    }
  };

  const validResolversForScope = editForm.scope ? (SCOPE_RESOLVER_MAP[editForm.scope] || []) : [];

  // Show loading skeleton while checking AI status
  if (isAiLoading) {
    return (
      <div className="px-4 sm:px-6 pb-6 pt-4">
        <PageHeader
          breadcrumb={[{ label: "Administration" }, { label: "AI Summaries" }]}
          title={
            <span className="flex items-center gap-2" data-testid="heading-summaries">
              <Sparkles className="w-6 h-6 text-primary" />
              AI Summaries
            </span>
          }
        />
        <div className="mt-6 space-y-4">
          <Skeleton className="h-10 w-64" />
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-72" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show empty state when AI is disabled
  if (!isAiEnabled) {
    return (
      <div className="px-4 sm:px-6 pb-6 pt-4">
        <PageHeader
          breadcrumb={[{ label: "Administration" }, { label: "AI Summaries" }]}
          title={
            <span className="flex items-center gap-2" data-testid="heading-summaries">
              <Sparkles className="w-6 h-6 text-primary" />
              AI Summaries
            </span>
          }
        />
        <Card className="mt-6">
          <CardContent className="py-12">
            <div className="text-center space-y-3">
              <Sparkles className="w-12 h-12 mx-auto text-muted-foreground" />
              <h3 className="text-lg font-medium">AI Summaries are Disabled</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                AI summaries have been disabled in system settings. An administrator can enable this feature in Settings &rarr; AI.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  /**
   * Render a section's text content, turning animal ID tokens (A######) into
   * clickable spans that open the animal detail drawer.
   */
  function renderMarkdown(text: string): React.ReactNode {
    // Render inline tokens: **bold** and animal IDs (A\d{4,8})
    function renderInline(s: string): React.ReactNode[] {
      return s.split(/(\*\*[^*]+\*\*|A\d{4,8})/g).map((token, idx) => {
        if (/^\*\*[^*]+\*\*$/.test(token)) {
          return <strong key={idx}>{token.slice(2, -2)}</strong>;
        }
        if (/^A\d{4,8}$/.test(token)) {
          return (
            <span
              key={idx}
              className="font-medium underline decoration-dotted cursor-pointer hover:text-foreground/80"
              onClick={() => { setAnimalDrawerId(token); setAnimalDrawerOpen(true); }}
              data-testid={`briefing-animal-link-${token}`}
            >
              {token}
            </span>
          );
        }
        return token;
      });
    }

    const lines = text.split('\n');
    const nodes: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
      const raw = lines[i];
      const trimmed = raw.trim();

      if (!trimmed) { i++; continue; }

      // ### or ## → section sub-heading
      if (trimmed.startsWith('### ') || trimmed.startsWith('## ')) {
        const headText = trimmed.replace(/^#{2,3} /, '');
        nodes.push(
          <h3 key={i} className="text-sm font-semibold mt-3 mb-1 text-foreground">
            {renderInline(headText)}
          </h3>
        );
        i++; continue;
      }

      // Numbered list item — gather optional indented sub-bullets
      const numMatch = trimmed.match(/^(\d+)\. (.*)/);
      if (numMatch) {
        const subItems: React.ReactNode[] = [];
        i++;
        while (i < lines.length && /^\s{2,}- /.test(lines[i])) {
          const subText = lines[i].replace(/^\s+- /, '');
          subItems.push(
            <li key={i} className="text-sm text-muted-foreground">
              {renderInline(subText)}
            </li>
          );
          i++;
        }
        nodes.push(
          <div key={`n-${i}`} className="text-sm">
            <p className="font-medium">{numMatch[1]}. {renderInline(numMatch[2])}</p>
            {subItems.length > 0 && (
              <ul className="list-none pl-4 mt-0.5 space-y-0.5">{subItems}</ul>
            )}
          </div>
        );
        continue;
      }

      // Top-level bullet list — gather consecutive bullet lines
      if (trimmed.startsWith('- ')) {
        const items: React.ReactNode[] = [];
        while (i < lines.length && lines[i].trim().startsWith('- ')) {
          items.push(
            <li key={i} className="text-sm">{renderInline(lines[i].trim().slice(2))}</li>
          );
          i++;
        }
        nodes.push(
          <ul key={`ul-${i}`} className="list-disc list-inside space-y-0.5">{items}</ul>
        );
        continue;
      }

      // Regular paragraph text
      nodes.push(
        <p key={i} className="text-sm leading-relaxed">{renderInline(trimmed)}</p>
      );
      i++;
    }

    return <div className="space-y-1.5">{nodes}</div>;
  }

  return (
    <div className="px-4 sm:px-6 pb-6 pt-4">
      <PageHeader
        breadcrumb={[{ label: "Administration" }, { label: "AI Summaries" }]}
        title={
          <span className="flex items-center gap-2" data-testid="heading-summaries">
            <Sparkles className="w-6 h-6 text-primary" />
            AI Summaries
          </span>
        }
        actions={
          groupPrompts.length > 0 ? (
            <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="button-generate-new">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Summary
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Generate AI Summary</DialogTitle>
                  <DialogDescription>
                    Select a prompt type and provide the required input to generate a new summary.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Prompt Type</Label>
                    <Select value={selectedPromptIdForGenerate || ""} onValueChange={setSelectedPromptIdForGenerate}>
                      <SelectTrigger data-testid="select-prompt-type">
                        <SelectValue placeholder="Select a prompt..." />
                      </SelectTrigger>
                      <SelectContent>
                        {groupPrompts.map(prompt => (
                          <SelectItem key={prompt.id} value={prompt.id}>
                            {prompt.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {selectedPromptIdForGenerate && promptsData?.prompts.find(p => p.id === selectedPromptIdForGenerate)?.resolver === "by_location_code" && (
                    <div className="space-y-2">
                      <Label>Location Code</Label>
                      <Select value={locationCode} onValueChange={setLocationCode}>
                        <SelectTrigger data-testid="select-location">
                          <SelectValue placeholder="Select location..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SALEM">Salem</SelectItem>
                          <SelectItem value="BOSTON">Boston</SelectItem>
                          <SelectItem value="METHUEN">Methuen</SelectItem>
                          <SelectItem value="CAPE">Cape</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
                  <Button 
                    onClick={handleGenerate} 
                    disabled={!selectedPromptIdForGenerate || generateMutation.isPending}
                    data-testid="button-confirm-generate"
                  >
                    {generateMutation.isPending ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : undefined
        }
      />

      <p className="text-muted-foreground mb-6">
        View and generate AI-powered summaries for animals and locations
      </p>

      <Tabs value={selectedTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="summaries" data-testid="tab-summaries">
            <FileText className="w-4 h-4 mr-2" />
            Summaries
          </TabsTrigger>
          <TabsTrigger value="briefings" data-testid="tab-briefings">
            <BookOpen className="w-4 h-4 mr-2" />
            Briefings
          </TabsTrigger>
          {user?.isAdmin && (
            <TabsTrigger value="prompts" data-testid="tab-prompt-library">
              <Database className="w-4 h-4 mr-2" />
              Prompt Library
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="summaries" className="mt-6">
          {/* H5.5: Filter Bar */}
          <Card className="mb-4">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search summaries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 max-w-xs"
                    data-testid="input-summaries-search"
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Select 
                    value={selectedLocation} 
                    onValueChange={setSelectedLocation}
                    disabled={isLocationsLoading}
                  >
                    <SelectTrigger className="w-[160px]" data-testid="select-summaries-location">
                      {isLocationsLoading ? (
                        <Skeleton className="h-4 w-24" />
                      ) : (
                        <SelectValue placeholder="Location" />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      {activeLocations.map(loc => (
                        <SelectItem key={loc.code} value={loc.code}>
                          {loc.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select 
                    value={selectedDays} 
                    onValueChange={setSelectedDays}
                  >
                    <SelectTrigger className="w-[150px]" data-testid="select-summaries-date-range">
                      <SelectValue placeholder="Date range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All time</SelectItem>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="14">Last 14 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                    </SelectContent>
                  </Select>
                  {(selectedLocation !== "all" || selectedDays !== "all" || searchQuery) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedLocation("all");
                        setSelectedDays("all");
                        setSearchQuery("");
                      }}
                      data-testid="button-summaries-clear-filters"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear all
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
          </Card>

          {summariesLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <Card key={i}>
                  <CardHeader><Skeleton className="h-5 w-48" /><Skeleton className="h-4 w-32" /></CardHeader>
                  <CardContent><Skeleton className="h-20 w-full" /></CardContent>
                </Card>
              ))}
            </div>
          ) : summariesData?.summaries && summariesData.summaries.length > 0 ? (
            <div className="space-y-4" data-testid="summaries-list">
              {summariesData.summaries.map(summary => (
                <Card key={summary.id} data-testid={`card-summary-${summary.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{summary.promptName || "Summary"}</CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          {getScopeBadge(summary.scope)}
                          <span className="text-muted-foreground">
                            {summary.resolvedIds.length} animal{summary.resolvedIds.length !== 1 ? "s" : ""}
                          </span>
                        </CardDescription>
                      </div>
                      {getStatusBadge(summary.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {summary.contentJson ? (
                      <div className="space-y-3">
                        {(() => {
                          // H5.3: Use normalization for resilient rendering
                          const normalized = normalizeSummaryForRender(summary.contentJson);
                          return (
                            <>
                              {normalized.interactionCount !== undefined && (
                                <p className="text-xs text-muted-foreground">
                                  {normalized.interactionCount} {normalized.interactionCount === 1 ? "interaction" : "interactions"} analyzed
                                </p>
                              )}
                              <ul className="list-disc list-inside space-y-2 text-sm">
                                {normalized.bullets.map((bullet, idx) => (
                                  <li key={idx}>{bullet}</li>
                                ))}
                              </ul>
                              {normalized.trendNarrative && (
                                <>
                                  <Separator />
                                  <p className="text-sm text-muted-foreground italic">{normalized.trendNarrative}</p>
                                </>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No content available</p>
                    )}
                  </CardContent>
                  
                  {/* H5.2 Rev 1.7: Sources Section */}
                  {summary.evidenceJson && (
                    <Collapsible 
                      open={expandedSources.has(summary.id)}
                      onOpenChange={(open) => {
                        setExpandedSources(prev => {
                          const next = new Set(prev);
                          if (open) next.add(summary.id);
                          else next.delete(summary.id);
                          return next;
                        });
                      }}
                    >
                      <div className="border-t px-6 py-2">
                        <CollapsibleTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="w-full justify-between px-2 h-8"
                            data-testid={`button-sources-${summary.id}`}
                          >
                            <span className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Info className="w-4 h-4" />
                              What data was used?
                            </span>
                            {expandedSources.has(summary.id) ? (
                              <ChevronDown className="w-4 h-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <div className="px-6 pb-4 space-y-4 bg-muted/30">
                          {/* Selection Method */}
                          <div className="pt-4">
                            <div className="flex items-center gap-2 text-sm font-medium mb-2">
                              <Filter className="w-4 h-4 text-primary" />
                              How animals were selected
                            </div>
                            <p className="text-sm text-muted-foreground ml-6">
                              {summary.evidenceJson.selectionMethod.description}
                            </p>
                            {summary.evidenceJson.selectionMethod.filters && (
                              <div className="flex flex-wrap gap-2 mt-2 ml-6">
                                {summary.evidenceJson.selectionMethod.filters.map((filter, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {filter.label}: {filter.value}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* Data Sources */}
                          <div>
                            <div className="flex items-center gap-2 text-sm font-medium mb-2">
                              <Server className="w-4 h-4 text-primary" />
                              Data sources used
                            </div>
                            <div className="space-y-2 ml-6">
                              {summary.evidenceJson.dataSources.map((source, idx) => (
                                <div key={idx} className="text-sm">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{source.name}</span>
                                    <Badge variant="outline" className="text-xs">
                                      {source.recordCount} records
                                    </Badge>
                                  </div>
                                  <p className="text-muted-foreground text-xs mt-0.5">
                                    {source.description}
                                  </p>
                                  {source.sampleFields && source.sampleFields.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {source.sampleFields.map((field, fIdx) => (
                                        <code key={fIdx} className="text-xs bg-muted px-1 py-0.5 rounded">
                                          {field}
                                        </code>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Processing Info */}
                          <div>
                            <div className="flex items-center gap-2 text-sm font-medium mb-2">
                              <Users className="w-4 h-4 text-primary" />
                              Analysis summary
                            </div>
                            <div className="text-sm text-muted-foreground ml-6 space-y-1">
                              <p>
                                <span className="font-medium">{summary.evidenceJson.processing.animalsIncluded}</span> animals analyzed
                              </p>
                              <p>
                                <span className="font-medium">{summary.evidenceJson.processing.dataPointsAnalyzed}</span> data points processed
                              </p>
                              {summary.evidenceJson.processing.timeframe && (
                                <p className="text-xs">
                                  Timeframe: {summary.evidenceJson.processing.timeframe}
                                </p>
                              )}
                              {summary.evidenceJson.processing.notes && summary.evidenceJson.processing.notes.length > 0 && (
                                <ul className="text-xs list-disc list-inside mt-2 space-y-0.5">
                                  {summary.evidenceJson.processing.notes.map((note, nIdx) => (
                                    <li key={nIdx}>{note}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                  
                  <CardFooter className="text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{summary.auditJson?.model || "Unknown model"}</Badge>
                      <span>Datasets: {summary.datasetsUsed.join(", ")}</span>
                    </div>
                    <span>Generated {format(new Date(summary.generatedAt), "MMM d, yyyy 'at' h:mm a")}</span>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (selectedLocation !== "all" || selectedDays !== "all" || searchQuery) ? (
            <Card>
              <CardContent className="py-0">
                <SearchEmptyState />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Sparkles className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium">No Summaries Yet</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                  AI summaries will appear here once generated. Individual animal summaries are shown on their detail pages.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Gate Q: Briefings Tab */}
        <TabsContent value="briefings" className="mt-6">
          {selectedBriefingId ? (
            /* ── Detail View ── */
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="ghost" size="sm" onClick={closeBriefing} data-testid="button-briefing-back">
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back to list
                </Button>
                {selectedBriefing && user?.isAdmin && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => regenerateBriefingMutation.mutate(selectedBriefing.id)}
                      disabled={regenerateBriefingMutation.isPending || selectedBriefing.status === "generating"}
                      data-testid="button-briefing-regenerate"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Regenerate
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => pinBriefingMutation.mutate({ id: selectedBriefing.id, isPinned: !selectedBriefing.isPinned })}
                      disabled={pinBriefingMutation.isPending}
                      data-testid="button-briefing-pin"
                    >
                      {selectedBriefing.isPinned ? <PinOff className="w-3 h-3 mr-1" /> : <Pin className="w-3 h-3 mr-1" />}
                      {selectedBriefing.isPinned ? "Unpin" : "Pin"}
                    </Button>
                  </>
                )}
              </div>

              {selectedBriefingLoading ? (
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <Skeleton className="h-6 w-64" />
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-32 w-full" />
                    </div>
                  </CardContent>
                </Card>
              ) : selectedBriefing ? (
                <>
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div>
                          <CardTitle className="text-base">{selectedBriefing.title}</CardTitle>
                          <CardDescription className="mt-1 flex items-center gap-2 flex-wrap">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {selectedBriefing.locationCode}
                            </span>
                            <span>{speciesLabel(selectedBriefing.species)}</span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {selectedBriefing.referenceDate}
                            </span>
                            {selectedBriefing.generatedAt && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Generated {format(new Date(selectedBriefing.generatedAt), "MMM d, yyyy 'at' h:mm a")}
                              </span>
                            )}
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedBriefing.isPinned && <Badge variant="secondary"><Pin className="w-3 h-3 mr-1" />Pinned</Badge>}
                          <Badge
                            variant={
                              selectedBriefing.status === "ready" ? "default" :
                              selectedBriefing.status === "partial" ? "secondary" :
                              selectedBriefing.status === "failed" ? "destructive" :
                              "outline"
                            }
                            data-testid={`briefing-status-${selectedBriefing.id}`}
                          >
                            {selectedBriefing.status === "generating" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                            {selectedBriefing.status}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>

                  {selectedBriefing.status === "generating" && (
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Generating briefing sections… This may take 30–60 seconds.</span>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {selectedBriefing.status === "failed" && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{selectedBriefing.errorMessage ?? "Briefing generation failed."}</AlertDescription>
                    </Alert>
                  )}

                  {selectedBriefing.contentStructured && selectedBriefing.contentStructured.length > 0 && (
                    <div className="space-y-3" data-testid="briefing-sections">
                      {selectedBriefing.contentStructured.map(section => (
                        <Card key={section.key} data-testid={`briefing-section-${section.key}`}>
                          <CardHeader className="pb-2">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <CardTitle className="text-base font-semibold">{section.title}</CardTitle>
                              {section.error && (
                                <Badge variant="destructive" className="text-xs">Failed</Badge>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0">
                            {section.error ? (
                              <p className="text-sm text-muted-foreground italic">{section.errorMessage ?? "This section could not be generated."}</p>
                            ) : (
                              <GenericJsonRenderer data={section.contentJson} />
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  {selectedBriefing.narrativeScript && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Narrative Script</CardTitle>
                        <CardDescription className="text-xs">Processed for spoken delivery</CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">{selectedBriefing.narrativeScript}</p>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : null}
            </div>
          ) : (
            /* ── List View ── */
            <div className="space-y-4">
              {/* Controls */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={briefingLocationFilter} onValueChange={setBriefingLocationFilter}>
                    <SelectTrigger className="w-[160px]" data-testid="select-briefing-location">
                      <SelectValue placeholder="All locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      {activeLocations.map(loc => (
                        <SelectItem key={loc.code} value={loc.code}>{loc.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {briefingLocationFilter !== "all" && (
                    <Button variant="ghost" size="sm" onClick={() => setBriefingLocationFilter("all")} data-testid="button-briefing-clear-filter">
                      <X className="w-3 h-3 mr-1" />Clear
                    </Button>
                  )}
                </div>
                <Button
                  onClick={() => {
                    setBriefingTemplateId(briefingTemplatesData?.templates.find(t => t.enabled)?.id ?? "");
                    setBriefingGenerateOpen(true);
                  }}
                  disabled={!isAiEnabled}
                  data-testid="button-briefing-generate"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Briefing
                </Button>
              </div>

              {/* Briefings list */}
              {briefingsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <Card key={i}><CardHeader><Skeleton className="h-5 w-64" /><Skeleton className="h-4 w-40 mt-1" /></CardHeader></Card>
                  ))}
                </div>
              ) : briefingsData?.briefings && briefingsData.briefings.length > 0 ? (
                <div className="space-y-2" data-testid="briefings-list">
                  {briefingsData.briefings.map(briefing => (
                    <Card
                      key={briefing.id}
                      className="cursor-pointer hover-elevate"
                      onClick={() => openBriefing(briefing.id)}
                      data-testid={`briefing-card-${briefing.id}`}
                    >
                      <CardHeader className="py-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {briefing.isPinned && <Pin className="w-3 h-3 text-muted-foreground shrink-0" />}
                              <span className="font-medium text-sm truncate">{briefing.title}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />{briefing.locationCode}
                              </span>
                              <span>{speciesLabel(briefing.species)}</span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />{briefing.referenceDate}
                              </span>
                              {briefing.generatedAt && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {format(new Date(briefing.generatedAt), "MMM d, h:mm a")}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={
                                briefing.status === "ready" ? "default" :
                                briefing.status === "partial" ? "secondary" :
                                briefing.status === "failed" ? "destructive" :
                                "outline"
                              }
                            >
                              {briefing.status === "generating" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                              {briefing.status}
                            </Badge>
                            <ArrowRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="pt-8 pb-8">
                    <div className="text-center space-y-2">
                      <BookOpen className="w-8 h-8 mx-auto text-muted-foreground" />
                      <p className="text-muted-foreground text-sm">No briefings yet. Generate your first briefing to get started.</p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setBriefingTemplateId(briefingTemplatesData?.templates.find(t => t.enabled)?.id ?? "");
                          setBriefingGenerateOpen(true);
                        }}
                        disabled={!isAiEnabled}
                        data-testid="button-briefing-generate-empty"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Briefing
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Admin: Briefing Templates section */}
              {user?.isAdmin && (
                <div className="mt-8">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Briefing Templates</h3>
                  {briefingTemplatesLoading ? (
                    <div className="space-y-2">
                      {[1, 2].map(i => <Card key={i}><CardHeader><Skeleton className="h-5 w-48" /></CardHeader></Card>)}
                    </div>
                  ) : briefingTemplatesData?.templates && briefingTemplatesData.templates.length > 0 ? (
                    <div className="space-y-2" data-testid="briefing-templates-list">
                      {briefingTemplatesData.templates.map(tmpl => (
                        <Card key={tmpl.id} data-testid={`briefing-template-${tmpl.id}`}>
                          <CardHeader className="py-3">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div>
                                <span className="font-medium text-sm">{tmpl.name}</span>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">{tmpl.model}</Badge>
                                  {tmpl.isBuiltIn && <Badge variant="secondary" className="text-xs">Built-in</Badge>}
                                  {!tmpl.enabled && <Badge variant="destructive" className="text-xs">Disabled</Badge>}
                                  {tmpl.templateConfigVersion !== undefined && (
                                    <Badge variant="outline" className="text-xs text-muted-foreground">v{tmpl.templateConfigVersion}</Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {tmpl.templateConfig && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedTemplate(tmpl);
                                      setEditingSectionKey(null);
                                      setSectionEditForm(null);
                                      setTemplateTopLevelForm({
                                        model: tmpl.model,
                                        systemInstructions: tmpl.template?.systemInstructions ?? "",
                                        styleGuidance: tmpl.template?.styleGuidance ?? "",
                                        narrativeInstructions: tmpl.templateConfig?.narrativeInstructions ?? "",
                                      });
                                      setTemplateDrawerOpen(true);
                                    }}
                                    data-testid={`button-briefing-configure-template-${tmpl.id}`}
                                  >
                                    <Pencil className="w-3 h-3 mr-1.5" />
                                    Configure Template
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setBriefingTemplateId(tmpl.id);
                                    setBriefingGenerateOpen(true);
                                  }}
                                  disabled={!isAiEnabled || !tmpl.enabled}
                                  data-testid={`button-briefing-use-template-${tmpl.id}`}
                                >
                                  Use Template
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No briefing templates configured.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Animal detail drawer — opened by clicking animal IDs in briefing sections */}
          {animalDrawerId && (
            <AnimalDetailDrawer
              open={animalDrawerOpen}
              onOpenChange={(open) => {
                setAnimalDrawerOpen(open);
                if (!open) setAnimalDrawerId(null);
              }}
              animalId={animalDrawerId}
            />
          )}
        </TabsContent>

        {/* Generate Briefing Dialog */}
        <Dialog open={briefingGenerateOpen} onOpenChange={setBriefingGenerateOpen}>
          <DialogContent data-testid="dialog-briefing-generate">
            <DialogHeader>
              <DialogTitle>Generate Briefing</DialogTitle>
              <DialogDescription>Select a location, species, and date to generate an AI briefing.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="briefing-template">Template</Label>
                <Select value={briefingTemplateId} onValueChange={setBriefingTemplateId}>
                  <SelectTrigger id="briefing-template" data-testid="select-briefing-template">
                    <SelectValue placeholder="Select template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {briefingTemplatesData?.templates.filter(t => t.enabled).map(tmpl => (
                      <SelectItem key={tmpl.id} value={tmpl.id}>{tmpl.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="briefing-location">Location</Label>
                <Select value={briefingLocationCode} onValueChange={setBriefingLocationCode}>
                  <SelectTrigger id="briefing-location" data-testid="select-briefing-location-generate">
                    <SelectValue placeholder="Select location…" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeLocations.map(loc => (
                      <SelectItem key={loc.code} value={loc.code}>{loc.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="briefing-species">Species</Label>
                <Select value={briefingSpecies} onValueChange={setBriefingSpecies}>
                  <SelectTrigger id="briefing-species" data-testid="select-briefing-species">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DOG">Dogs</SelectItem>
                    <SelectItem value="CAT">Cats</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="briefing-date">Reference Date</Label>
                <Input
                  id="briefing-date"
                  type="date"
                  value={briefingDate}
                  onChange={e => setBriefingDate(e.target.value)}
                  data-testid="input-briefing-date"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBriefingGenerateOpen(false)} data-testid="button-briefing-generate-cancel">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!briefingTemplateId || !briefingLocationCode || !briefingSpecies || !briefingDate) return;
                  generateBriefingMutation.mutate({
                    promptDefinitionId: briefingTemplateId,
                    locationCode: briefingLocationCode,
                    species: briefingSpecies,
                    referenceDate: briefingDate,
                  });
                }}
                disabled={!briefingTemplateId || !briefingLocationCode || !briefingSpecies || !briefingDate || generateBriefingMutation.isPending}
                data-testid="button-briefing-generate-confirm"
              >
                {generateBriefingMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Starting…</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Generate</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Gate Q1.5: Briefing Template Section Editor Sheet */}
        <Sheet open={templateDrawerOpen} onOpenChange={(open) => {
          if (!open) {
            setEditingSectionKey(null);
            setSectionEditForm(null);
            setTemplateTopLevelForm(null);
          }
          setTemplateDrawerOpen(open);
        }}>
          <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="drawer-briefing-configure-template">
            <SheetHeader>
              <SheetTitle>Configure — {selectedTemplate?.name ?? "Briefing Template"}</SheetTitle>
              <SheetDescription className="flex items-center gap-2 flex-wrap">
                {selectedTemplate?.isBuiltIn && <Badge variant="outline" className="text-xs">Built-in</Badge>}
                {selectedTemplate?.model && <Badge variant="secondary" className="text-xs">{selectedTemplate.model}</Badge>}
                {selectedTemplate?.templateConfigVersion !== undefined && (
                  <span className="text-xs text-muted-foreground">v{selectedTemplate.templateConfigVersion}</span>
                )}
              </SheetDescription>
            </SheetHeader>

            {/* Gate Q2 Phase 1 — UX-1 / LW-1: Top-level template settings panel */}
            <div className="mt-4 space-y-4">
              <div>
                <h3 className="text-sm font-medium">Template Settings</h3>
                <p className="text-xs text-muted-foreground mt-1">Global instructions, style, and model for this template. Section-specific instructions are configured on each section card below.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Model</Label>
                <Select
                  value={templateTopLevelForm?.model ?? ""}
                  onValueChange={(v) => setTemplateTopLevelForm(f => f ? { ...f, model: v } : f)}
                >
                  <SelectTrigger className="text-xs" data-testid="select-template-model">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(promptsData?.allowLists.models ?? []).map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Briefing Purpose Instructions</Label>
                <p className="text-xs text-muted-foreground">Describes the purpose and role of this briefing template. Applied to every section and the narrative pass. Distinct from the app-wide global system prompt set in AI Settings.</p>
                <Textarea
                  rows={5}
                  value={templateTopLevelForm?.systemInstructions ?? ""}
                  onChange={e => setTemplateTopLevelForm(f => f ? { ...f, systemInstructions: e.target.value } : f)}
                  className="text-xs font-mono resize-y"
                  data-testid="textarea-template-system-instructions"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Style Guidance</Label>
                <p className="text-xs text-muted-foreground">Tone and style applied to all sections and the narrative pass for this template.</p>
                <Textarea
                  rows={3}
                  value={templateTopLevelForm?.styleGuidance ?? ""}
                  onChange={e => setTemplateTopLevelForm(f => f ? { ...f, styleGuidance: e.target.value } : f)}
                  className="text-xs font-mono resize-y"
                  data-testid="textarea-template-style-guidance"
                />
              </div>
            </div>

            {/* Gate Q2 Phase 1 — UX-2: Narrative Script panel */}
            <div className="mt-6 space-y-3 border-t pt-5">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-medium">Narrative Script</h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    if (!selectedTemplate) return;
                    setNarrativeResetLoading(true);
                    try {
                      const res = await apiRequest("GET", `/api/summaries/prompts/${selectedTemplate.id}/template-config/defaults`);
                      setTemplateTopLevelForm(f => f ? { ...f, narrativeInstructions: res.narrativeInstructions ?? "" } : f);
                      toast({ title: "Defaults loaded", description: "Review the text below and click Save to apply." });
                    } catch {
                      toast({ title: "Reset failed", description: "Could not fetch defaults.", variant: "destructive" });
                    } finally {
                      setNarrativeResetLoading(false);
                    }
                  }}
                  disabled={narrativeResetLoading || updateTemplateMutation.isPending}
                  data-testid="button-narrative-reset"
                >
                  {narrativeResetLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                  Reset to defaults
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Controls how the AI rewrites briefing sections into a spoken narrative. The model receives cleaned text from all enabled sections as input.</p>
              <Textarea
                rows={10}
                value={templateTopLevelForm?.narrativeInstructions ?? ""}
                onChange={e => setTemplateTopLevelForm(f => f ? { ...f, narrativeInstructions: e.target.value } : f)}
                className="text-xs font-mono resize-y"
                data-testid="textarea-narrative-instructions"
              />
            </div>

            {/* Sections */}
            <div className="mt-6 border-t pt-5 space-y-3">
              <div>
                <h3 className="text-sm font-medium">Sections</h3>
                <p className="text-xs text-muted-foreground mt-1">Each section is generated as a separate prompt call. Use the Edit button on each card to update per-section instructions and format constraints.</p>
              </div>
              {(selectedTemplate?.templateConfig?.sections ?? [])
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((section, idx, sorted) => {
                  const isEditing = editingSectionKey === section.key;
                  return (
                    <Card key={section.key} data-testid={`section-card-${section.key}`}>
                      <CardHeader className="pb-2 pt-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{section.title}</span>
                            {!section.enabled && <Badge variant="secondary" className="text-xs">Disabled</Badge>}
                          </div>
                          {!isEditing && (
                            <div className="flex items-center gap-1.5">
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => selectedTemplate && reorderSectionMutation.mutate({ templateId: selectedTemplate.id, sectionKey: section.key, direction: "up" })}
                                disabled={idx === 0 || reorderSectionMutation.isPending}
                                data-testid={`button-section-move-up-${section.key}`}
                              >
                                <ArrowUp className="w-3 h-3" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => selectedTemplate && reorderSectionMutation.mutate({ templateId: selectedTemplate.id, sectionKey: section.key, direction: "down" })}
                                disabled={idx === sorted.length - 1 || reorderSectionMutation.isPending}
                                data-testid={`button-section-move-down-${section.key}`}
                              >
                                <ArrowDown className="w-3 h-3" />
                              </Button>
                              {selectedTemplate?.isBuiltIn && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setResetSectionKey(section.key);
                                    setSectionResetDialogOpen(true);
                                  }}
                                  data-testid={`button-section-reset-${section.key}`}
                                >
                                  <RotateCcw className="w-3 h-3 mr-1" />
                                  Reset
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingSectionKey(section.key);
                                  setSectionEditForm({
                                    systemInstructions: section.systemInstructions ?? "",
                                    formatConstraints: section.formatConstraints ?? "",
                                    lookbackDays: section.lookbackDays?.toString() ?? "",
                                    overdueAnimalCap: section.overdueAnimalCap?.toString() ?? "",
                                    enabled: section.enabled,
                                  });
                                }}
                                data-testid={`button-section-edit-${section.key}`}
                              >
                                <Pencil className="w-3 h-3 mr-1" />
                                Edit
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-wrap mt-1">
                          {section.datasets.map(ds => (
                            <Badge key={ds} variant="outline" className="text-xs">{ds}</Badge>
                          ))}
                        </div>
                      </CardHeader>

                      {isEditing && sectionEditForm ? (
                        <CardContent className="pt-0 space-y-3">
                          <div className="space-y-1.5">
                            <Label htmlFor={`si-${section.key}`} className="text-xs">System Instructions</Label>
                            <Textarea
                              id={`si-${section.key}`}
                              rows={8}
                              value={sectionEditForm.systemInstructions}
                              onChange={e => setSectionEditForm(f => f ? { ...f, systemInstructions: e.target.value } : f)}
                              className="text-xs font-mono resize-y"
                              data-testid={`textarea-system-instructions-${section.key}`}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor={`fc-${section.key}`} className="text-xs">Format Constraints</Label>
                            <Textarea
                              id={`fc-${section.key}`}
                              rows={3}
                              value={sectionEditForm.formatConstraints}
                              onChange={e => setSectionEditForm(f => f ? { ...f, formatConstraints: e.target.value } : f)}
                              className="text-xs font-mono resize-y"
                              data-testid={`textarea-format-constraints-${section.key}`}
                            />
                          </div>
                          {section.lookbackDays !== undefined && (
                            <div className="space-y-1.5">
                              <Label htmlFor={`lb-${section.key}`} className="text-xs">Lookback Days</Label>
                              <Input
                                id={`lb-${section.key}`}
                                type="number"
                                min={1}
                                max={30}
                                value={sectionEditForm.lookbackDays}
                                onChange={e => setSectionEditForm(f => f ? { ...f, lookbackDays: e.target.value } : f)}
                                className="text-xs w-24"
                                data-testid={`input-lookback-days-${section.key}`}
                              />
                              <p className="text-xs text-muted-foreground">Days of interaction history included (1–30)</p>
                            </div>
                          )}
                          {section.overdueAnimalCap !== undefined && (
                            <div className="space-y-1.5">
                              <Label htmlFor={`oc-${section.key}`} className="text-xs">Overdue Animal Cap</Label>
                              <Input
                                id={`oc-${section.key}`}
                                type="number"
                                min={1}
                                max={100}
                                value={sectionEditForm.overdueAnimalCap}
                                onChange={e => setSectionEditForm(f => f ? { ...f, overdueAnimalCap: e.target.value } : f)}
                                className="text-xs w-24"
                                data-testid={`input-overdue-cap-${section.key}`}
                              />
                              <p className="text-xs text-muted-foreground">Max overdue animals sent to model (1–100)</p>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={sectionEditForm.enabled}
                              onCheckedChange={v => setSectionEditForm(f => f ? { ...f, enabled: v } : f)}
                              id={`enabled-${section.key}`}
                              data-testid={`switch-section-enabled-${section.key}`}
                            />
                            <Label htmlFor={`enabled-${section.key}`} className="text-xs">Enabled</Label>
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <Button
                              size="sm"
                              onClick={() => {
                                if (!selectedTemplate || !sectionEditForm) return;
                                const lkd = sectionEditForm.lookbackDays ? parseInt(sectionEditForm.lookbackDays, 10) : null;
                                const oac = sectionEditForm.overdueAnimalCap ? parseInt(sectionEditForm.overdueAnimalCap, 10) : null;
                                patchSectionMutation.mutate({
                                  templateId: selectedTemplate.id,
                                  sectionKey: section.key,
                                  data: {
                                    systemInstructions: sectionEditForm.systemInstructions,
                                    formatConstraints: sectionEditForm.formatConstraints || undefined,
                                    ...(lkd !== null && !isNaN(lkd) ? { lookbackDays: lkd } : {}),
                                    ...(oac !== null && !isNaN(oac) ? { overdueAnimalCap: oac } : {}),
                                    enabled: sectionEditForm.enabled,
                                  },
                                });
                              }}
                              disabled={patchSectionMutation.isPending || !sectionEditForm.systemInstructions.trim()}
                              data-testid={`button-section-save-${section.key}`}
                            >
                              {patchSectionMutation.isPending ? (
                                <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Saving…</>
                              ) : (
                                <><Save className="w-3 h-3 mr-1" />Save</>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setEditingSectionKey(null); setSectionEditForm(null); }}
                              disabled={patchSectionMutation.isPending}
                              data-testid={`button-section-cancel-${section.key}`}
                            >
                              Cancel
                            </Button>
                          </div>
                        </CardContent>
                      ) : (
                        <CardContent className="pt-0">
                          <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">{section.systemInstructions}</p>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
            </div>

            <SheetFooter className="flex-col sm:flex-row gap-2 border-t pt-4 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  if (selectedTemplate) {
                    setTemplateTopLevelForm({
                      model: selectedTemplate.model,
                      systemInstructions: selectedTemplate.template?.systemInstructions ?? "",
                      styleGuidance: selectedTemplate.template?.styleGuidance ?? "",
                      narrativeInstructions: selectedTemplate.templateConfig?.narrativeInstructions ?? "",
                    });
                  }
                }}
                disabled={updateTemplateMutation.isPending}
                data-testid="button-template-discard"
              >
                Discard changes
              </Button>
              <Button
                onClick={() => {
                  if (!selectedTemplate || !templateTopLevelForm) return;
                  updateTemplateMutation.mutate({
                    templateId: selectedTemplate.id,
                    data: {
                      model: templateTopLevelForm.model,
                      template: {
                        systemInstructions: templateTopLevelForm.systemInstructions,
                        styleGuidance: templateTopLevelForm.styleGuidance,
                      },
                      templateConfig: {
                        sections: selectedTemplate.templateConfig?.sections ?? [],
                        narrativeInstructions: templateTopLevelForm.narrativeInstructions,
                      },
                    },
                  });
                }}
                disabled={updateTemplateMutation.isPending || !templateTopLevelForm}
                data-testid="button-template-save"
              >
                {updateTemplateMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />Save</>
                )}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Section reset confirmation dialog */}
        <AlertDialog open={sectionResetDialogOpen} onOpenChange={setSectionResetDialogOpen}>
          <AlertDialogContent data-testid="dialog-section-reset">
            <AlertDialogHeader>
              <AlertDialogTitle>Reset Section to Defaults?</AlertDialogTitle>
              <AlertDialogDescription>
                This will restore the factory system instructions and format constraints for this section.
                The section's enabled/disabled state and position will be preserved.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="button-section-reset-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (!selectedTemplate || !resetSectionKey) return;
                  resetSectionMutation.mutate({ templateId: selectedTemplate.id, sectionKey: resetSectionKey });
                }}
                disabled={resetSectionMutation.isPending}
                data-testid="button-section-reset-confirm"
              >
                {resetSectionMutation.isPending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                Reset to Defaults
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {user?.isAdmin && (
          <TabsContent value="prompts" className="mt-6">
            {promptsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <Card key={i}><CardHeader><Skeleton className="h-5 w-48" /><Skeleton className="h-4 w-32" /></CardHeader></Card>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-sm text-muted-foreground">
                    {promptsData?.prompts.length || 0} prompt definition{(promptsData?.prompts.length || 0) !== 1 ? "s" : ""}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => { setAiDraftDrawerOpen(true); fetchSuggestions(); }} data-testid="button-create-with-ai">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Create with AI
                    </Button>
                    <Button onClick={openNewPromptDrawer} data-testid="button-new-prompt">
                      <Plus className="w-4 h-4 mr-2" />
                      New Prompt
                    </Button>
                  </div>
                </div>
                
                {promptsData?.prompts.map(prompt => (
                  <Card 
                    key={prompt.id} 
                    className="cursor-pointer hover-elevate transition-colors"
                    onClick={() => openPromptDrawer(prompt)}
                    data-testid={`prompt-row-${prompt.id}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                            <span className="truncate">{prompt.name}</span>
                            {prompt.isBuiltIn && <Badge variant="outline" className="text-xs shrink-0">Built-in</Badge>}
                          </CardTitle>
                          <CardDescription className="mt-1 flex items-center gap-2 flex-wrap">
                            {getScopeBadge(prompt.scope)}
                            <span className="text-xs">{prompt.resolver}</span>
                            <span className="text-xs text-muted-foreground">({prompt.datasets.length} dataset{prompt.datasets.length !== 1 ? "s" : ""})</span>
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant={prompt.visibility === "admin" ? "secondary" : "outline"} className="text-xs capitalize">
                            {prompt.visibility}
                          </Badge>
                          <Badge variant={prompt.enabled ? "default" : "secondary"} className="text-xs">
                            {prompt.enabled ? "Enabled" : "Disabled"}
                          </Badge>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" data-testid={`button-prompt-actions-${prompt.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                              {prompt.isBriefingTemplate ? (
                                <DropdownMenuItem onClick={() => openPromptDrawer(prompt)} data-testid={`menu-view-${prompt.id}`}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View details
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => {
                                  openPromptDrawer(prompt);
                                  setTimeout(() => setIsEditing(true), 100);
                                }} data-testid={`menu-edit-${prompt.id}`}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => duplicateMutation.mutate(prompt.id)} data-testid={`menu-duplicate-${prompt.id}`}>
                                <Copy className="w-4 h-4 mr-2" />
                                Duplicate
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {prompt.isBuiltIn ? (
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setActionPrompt(prompt);
                                    setResetDialogOpen(true);
                                  }} 
                                  data-testid={`menu-reset-${prompt.id}`}
                                >
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Reset to Defaults
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem 
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => {
                                    setActionPrompt(prompt);
                                    setDeleteDialogOpen(true);
                                  }}
                                  data-testid={`menu-delete-${prompt.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Prompt Detail/Edit Sheet */}
      <Sheet open={drawerOpen} onOpenChange={(open) => {
        if (!open) {
          setIsEditing(false);
          setIsCreating(false);
        }
        setDrawerOpen(open);
      }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid={isCreating ? "drawer-prompt-new" : `drawer-prompt-${selectedPrompt?.id}`}>
          <SheetHeader>
            <SheetTitle>{isCreating ? "New Prompt Definition" : selectedPrompt?.name}</SheetTitle>
            <SheetDescription>
              {isCreating ? "Create a new custom prompt definition" : (isEditing ? "Edit prompt configuration" : "View prompt configuration")}
            </SheetDescription>
            {!isCreating && !isEditing && selectedPrompt && (
              <p className="text-xs text-muted-foreground mt-2">
                {selectedPrompt.isBriefingTemplate
                  ? "This template is managed from the Briefings tab. Configure sections, settings, and the narrative script there."
                  : selectedPrompt.isBuiltIn
                  ? "Built-in prompts can be edited, duplicated, or reset to defaults, but cannot be deleted."
                  : "Custom prompts support full editing, duplication, and deletion."}
              </p>
            )}
          </SheetHeader>

          {editError && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{editError}</AlertDescription>
            </Alert>
          )}

          <div className="py-6 space-y-6">
            {(isEditing || isCreating) ? (
              <>
                {/* Edit Mode */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="prompt-name">Name</Label>
                    <Input 
                      id="prompt-name"
                      value={editForm.name || ""} 
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                      data-testid="input-prompt-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="prompt-intent">Intent</Label>
                    <Input 
                      id="prompt-intent"
                      value={editForm.intent || ""} 
                      onChange={(e) => setEditForm(prev => ({ ...prev, intent: e.target.value }))}
                      placeholder="e.g., behavior_summary, population_overview"
                      data-testid="select-prompt-intent"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Scope</Label>
                      <Select value={editForm.scope || ""} onValueChange={handleScopeChange}>
                        <SelectTrigger data-testid="select-prompt-scope">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {promptsData?.allowLists.scopes.map(scope => (
                            <SelectItem key={scope} value={scope}>{scope}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Resolver</Label>
                      <Select value={editForm.resolver || ""} onValueChange={(v) => setEditForm(prev => ({ ...prev, resolver: v }))}>
                        <SelectTrigger data-testid="select-prompt-resolver">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {validResolversForScope.map(resolver => (
                            <SelectItem key={resolver} value={resolver}>{resolver}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Model</Label>
                      <Select value={editForm.model || ""} onValueChange={(v) => setEditForm(prev => ({ ...prev, model: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {promptsData?.allowLists.models.map(model => (
                            <SelectItem key={model} value={model}>{model}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Visibility</Label>
                      <Select value={editForm.visibility || "admin"} onValueChange={(v: "admin" | "staff") => setEditForm(prev => ({ ...prev, visibility: v }))}>
                        <SelectTrigger data-testid="select-prompt-visibility">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="staff">Staff</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Datasets</Label>
                    <div className="flex flex-wrap gap-2" data-testid="multiselect-prompt-datasets">
                      {promptsData?.allowLists.datasets.map(dataset => (
                        <Badge 
                          key={dataset}
                          variant={editForm.datasets?.includes(dataset) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => handleDatasetToggle(dataset)}
                        >
                          {dataset}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="prompt-enabled">Enabled</Label>
                    <Switch 
                      id="prompt-enabled"
                      checked={editForm.enabled || false}
                      onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, enabled: checked }))}
                      data-testid="toggle-enabled-prompt"
                    />
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <h4 className="text-sm font-medium">Template Configuration</h4>

                    <div className="space-y-2">
                      <Label htmlFor="template-system">System Instructions</Label>
                      <Textarea 
                        id="template-system"
                        value={editForm.template?.systemInstructions || ""} 
                        onChange={(e) => setEditForm(prev => ({ 
                          ...prev, 
                          template: { ...prev.template!, systemInstructions: e.target.value }
                        }))}
                        rows={3}
                        data-testid="textarea-template-system"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="template-style">Style Guidance</Label>
                      <Textarea 
                        id="template-style"
                        value={editForm.template?.styleGuidance || ""} 
                        onChange={(e) => setEditForm(prev => ({ 
                          ...prev, 
                          template: { ...prev.template!, styleGuidance: e.target.value }
                        }))}
                        rows={2}
                        data-testid="textarea-template-style"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="template-format">Format Constraints</Label>
                      <Textarea 
                        id="template-format"
                        value={editForm.template?.formatConstraints || ""} 
                        onChange={(e) => setEditForm(prev => ({ 
                          ...prev, 
                          template: { ...prev.template!, formatConstraints: e.target.value }
                        }))}
                        rows={4}
                        data-testid="textarea-template-format"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="length-min">Min Length (tokens)</Label>
                        <Input 
                          id="length-min"
                          type="number"
                          value={editForm.template?.lengthBounds?.min || 0} 
                          onChange={(e) => setEditForm(prev => ({ 
                            ...prev, 
                            template: { ...prev.template!, lengthBounds: { ...prev.template!.lengthBounds, min: parseInt(e.target.value) || 0 } }
                          }))}
                          data-testid="input-length-min"
                        />
                        <p className="text-xs text-muted-foreground">Stored for reference only — not enforced during generation.</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="length-max">Max Length (tokens)</Label>
                        <Input 
                          id="length-max"
                          type="number"
                          value={editForm.template?.lengthBounds?.max || 500} 
                          onChange={(e) => setEditForm(prev => ({ 
                            ...prev, 
                            template: { ...prev.template!, lengthBounds: { ...prev.template!.lengthBounds, max: parseInt(e.target.value) || 500 } }
                          }))}
                          data-testid="input-length-max"
                        />
                        <p className="text-xs text-muted-foreground">Controls the generation budget — the model will not exceed this.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : selectedPrompt && (
              <>
                {/* View Mode */}
                <div className="space-y-4">
                  {selectedPrompt.isBriefingTemplate ? (
                    <>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Model:</span>
                          <p className="font-medium">{selectedPrompt.model}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Status:</span>
                          <p className="font-medium">{selectedPrompt.enabled ? "Enabled" : "Disabled"}</p>
                        </div>
                      </div>
                      {selectedPrompt.template?.systemInstructions ? (
                        <div>
                          <span className="text-xs text-muted-foreground">Briefing Purpose Instructions:</span>
                          <p className="text-sm mt-1 bg-muted p-2 rounded text-muted-foreground whitespace-pre-wrap">{selectedPrompt.template.systemInstructions}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">No briefing purpose instructions set.</p>
                      )}
                      {selectedPrompt.template?.styleGuidance && (
                        <div>
                          <span className="text-xs text-muted-foreground">Style Guidance:</span>
                          <p className="text-sm mt-1 bg-muted p-2 rounded text-muted-foreground whitespace-pre-wrap">{selectedPrompt.template.styleGuidance}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Intent:</span>
                          <p className="font-medium">{selectedPrompt.intent}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Model:</span>
                          <p className="font-medium">{selectedPrompt.model}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Scope:</span>
                          <p className="font-medium">{selectedPrompt.scope}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Resolver:</span>
                          <p className="font-medium">{selectedPrompt.resolver}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Visibility:</span>
                          <p className="font-medium capitalize">{selectedPrompt.visibility}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Status:</span>
                          <p className="font-medium">{selectedPrompt.enabled ? "Enabled" : "Disabled"}</p>
                        </div>
                      </div>

                      <div>
                        <span className="text-sm text-muted-foreground">Datasets:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedPrompt.datasets.map(ds => (
                            <Badge key={ds} variant="outline" className="text-xs">{ds}</Badge>
                          ))}
                        </div>
                      </div>

                      {selectedPrompt.template && (
                        <>
                          <Separator />
                          <div className="space-y-3">
                            <h4 className="text-sm font-medium">Template</h4>
                            <div>
                              <span className="text-xs text-muted-foreground">System Instructions:</span>
                              <p className="text-sm mt-1 bg-muted p-2 rounded text-muted-foreground">{selectedPrompt.template.systemInstructions}</p>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground">Style Guidance:</span>
                              <p className="text-sm mt-1 bg-muted p-2 rounded text-muted-foreground">{selectedPrompt.template.styleGuidance}</p>
                            </div>
                            <div>
                              <span className="text-xs text-muted-foreground">Format Constraints:</span>
                              <p className="text-sm mt-1 bg-muted p-2 rounded whitespace-pre-wrap text-muted-foreground">{selectedPrompt.template.formatConstraints}</p>
                            </div>
                            {selectedPrompt.template.lengthBounds && (
                              <div className="flex gap-4 text-sm">
                                <span className="text-muted-foreground">Length bounds: {selectedPrompt.template.lengthBounds.min} - {selectedPrompt.template.lengthBounds.max} tokens</span>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          <SheetFooter className="flex-col sm:flex-row gap-2 border-t pt-4">
            {(isEditing || isCreating) ? (
              <>
                <Button variant="outline" onClick={handleCancel} className="w-full sm:w-auto" data-testid="button-cancel-prompt">
                  <X className="w-4 h-4 mr-2" />Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="w-full sm:w-auto"
                  data-testid="button-save-prompt"
                >
                  {(createMutation.isPending || updateMutation.isPending) ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" />Save</>
                  )}
                </Button>
              </>
            ) : selectedPrompt && (
              <>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button variant="outline" onClick={() => duplicateMutation.mutate(selectedPrompt.id)} disabled={duplicateMutation.isPending}>
                    <Copy className="w-4 h-4 mr-2" />Duplicate
                  </Button>
                  {selectedPrompt.isBuiltIn ? (
                    <Button variant="outline" onClick={() => setResetDialogOpen(true)}>
                      <RotateCcw className="w-4 h-4 mr-2" />Reset
                    </Button>
                  ) : (
                    <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)} data-testid="button-delete-prompt">
                      <Trash2 className="w-4 h-4 mr-2" />Delete
                    </Button>
                  )}
                </div>
                {!selectedPrompt.isBriefingTemplate && (
                  <Button onClick={() => setIsEditing(true)} className="w-full sm:w-auto" data-testid="button-edit-prompt">
                    <Pencil className="w-4 h-4 mr-2" />Edit
                  </Button>
                )}
              </>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) setActionPrompt(null);
      }}>
        <AlertDialogContent data-testid="confirm-delete-prompt">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Prompt Definition</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{(actionPrompt || selectedPrompt)?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                const prompt = actionPrompt || selectedPrompt;
                if (prompt) deleteMutation.mutate(prompt.id);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Confirmation Dialog */}
      <AlertDialog open={resetDialogOpen} onOpenChange={(open) => {
        setResetDialogOpen(open);
        if (!open) setActionPrompt(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to Factory Defaults</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset "{(actionPrompt || selectedPrompt)?.name}" to its original factory configuration. All customizations will be lost. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                const prompt = actionPrompt || selectedPrompt;
                if (prompt) resetMutation.mutate(prompt.id);
              }}
            >
              {resetMutation.isPending ? "Resetting..." : "Reset to Defaults"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI Draft Drawer (H5.2) */}
      <Sheet open={aiDraftDrawerOpen} onOpenChange={(open) => {
        if (!open) closeAiDraftDrawer();
      }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="drawer-ai-draft">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Create Summary Prompt with AI
            </SheetTitle>
            <SheetDescription>
              Describe what you want in plain English, then review and edit the generated draft.
            </SheetDescription>
          </SheetHeader>

          <Tabs value={aiDraftTab} onValueChange={(v) => setAiDraftTab(v as "describe" | "review")} className="mt-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="describe" disabled={aiDescriptionLocked && aiDraftTab === "review"} data-testid="tab-ai-describe">
                <FileText className="w-4 h-4 mr-2" />
                Describe
              </TabsTrigger>
              <TabsTrigger value="review" disabled={!aiDraftResult} data-testid="tab-ai-review">
                <Eye className="w-4 h-4 mr-2" />
                Review
              </TabsTrigger>
            </TabsList>

            <TabsContent value="describe" className="mt-4 space-y-4">
              {/* Accepted Filters Help Text */}
              <div className="text-sm space-y-3 p-3 bg-muted/50 rounded-lg">
                <p className="font-medium text-foreground">You can mention any of these:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-muted-foreground">
                  <div>
                    <p className="font-medium text-foreground/80">Animals</p>
                    <ul className="text-xs space-y-0.5 ml-2">
                      <li>Species: dogs, cats</li>
                      <li>Breed, sex (male/female)</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-foreground/80">Location</p>
                    <ul className="text-xs space-y-0.5 ml-2">
                      <li>Boston, Salem, Methuen, Cape</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-foreground/80">Behavior Scores</p>
                    <ul className="text-xs space-y-0.5 ml-2">
                      <li>Score: 1, 2, 3A, 3B, 4A, 4B</li>
                      <li>Severity: green, yellow, red</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-foreground/80">Time & Analysis</p>
                    <ul className="text-xs space-y-0.5 ml-2">
                      <li>Past 7/14/30 days</li>
                      <li>Behavior trends</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* AI Suggestions */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Try one of these suggestions</Label>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => fetchSuggestions(true)}
                    disabled={suggestionsLoading}
                    className="h-7 text-xs"
                    data-testid="button-refresh-suggestions"
                  >
                    <RefreshCw className={`w-3 h-3 mr-1 ${suggestionsLoading ? "animate-spin" : ""}`} />
                    {suggestionsLoading ? "Loading..." : "Refresh"}
                  </Button>
                </div>
                {suggestionsLoading ? (
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-8 w-32" />
                    ))}
                  </div>
                ) : aiSuggestions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {aiSuggestions.map((suggestion, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        className="h-auto py-1.5 px-3 text-xs text-left whitespace-normal"
                        onClick={() => setAiDescription(suggestion)}
                        disabled={aiDescriptionLocked}
                        data-testid={`button-suggestion-${idx}`}
                      >
                        <Lightbulb className="w-3 h-3 mr-1.5 flex-shrink-0" />
                        {suggestion}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No suggestions available. Click Refresh to generate ideas.</p>
                )}
              </div>

              {/* Description Input */}
              <div className="space-y-2">
                <Label htmlFor="ai-description">Describe the summary that you would like to see</Label>
                <Textarea
                  id="ai-description"
                  placeholder="Example: Weekly summary of behavior trends for dogs at Boston, focusing on aggression and socialization..."
                  value={aiDescription}
                  onChange={(e) => setAiDescription(e.target.value)}
                  disabled={aiDescriptionLocked}
                  className="min-h-[120px]"
                  maxLength={1000}
                  data-testid="input-ai-description"
                />
                <p className="text-xs text-muted-foreground">{aiDescription.length}/1000 characters</p>
              </div>

              {/* Error Display */}
              {aiDraftError && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md" data-testid="ai-draft-error">
                  <p className="text-sm text-destructive font-medium">Generation Failed</p>
                  <p className="text-sm text-destructive/80">{aiDraftError}</p>
                </div>
              )}

              {/* Generate Button */}
              <Button 
                onClick={handleAiDraftGenerate}
                disabled={!aiDescription.trim() || aiDraftMutation.isPending}
                className="w-full"
                data-testid="button-generate-ai-draft"
              >
                {aiDraftMutation.isPending ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Generating Draft...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Generate Summary Prompt</>
                )}
              </Button>
            </TabsContent>

            <TabsContent value="review" className="mt-4 space-y-4">
              {aiDraftResult && (
                <>
                  {/* Locked Description Preview */}
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs text-muted-foreground">Your Description</Label>
                      <Button variant="ghost" size="sm" onClick={resetAiDraft} data-testid="button-start-over">
                        <RotateCcw className="w-3 h-3 mr-1" />
                        Start Over
                      </Button>
                    </div>
                    <p className="text-sm italic">"{aiDescription}"</p>
                  </div>

                  {/* Confidence Badge */}
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Generated Draft</h4>
                    <Badge variant={aiDraftResult.confidence >= 0.7 ? "default" : "secondary"}>
                      {Math.round(aiDraftResult.confidence * 100)}% confidence
                    </Badge>
                  </div>

                  {/* Warnings */}
                  {aiDraftResult.warnings.length > 0 && (
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-md">
                      <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400 mb-1">Warnings</p>
                      <ul className="text-sm text-yellow-600 dark:text-yellow-300 list-disc list-inside space-y-1">
                        {aiDraftResult.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Draft Preview */}
                  <div className="space-y-3 text-sm border rounded-lg p-4" data-testid="ai-draft-result">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">Name</Label>
                        <p className="font-medium">{aiDraftResult.draft.name}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Intent</Label>
                        <p className="font-medium">{aiDraftResult.draft.intent}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">Scope</Label>
                        <p>{getScopeBadge(aiDraftResult.draft.scope as string)}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Resolver</Label>
                        <Badge variant="outline">{aiDraftResult.draft.resolver}</Badge>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">Datasets</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {aiDraftResult.draft.datasets?.map(ds => (
                          <Badge key={ds} variant="secondary" className="text-xs">{ds}</Badge>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">Model</Label>
                      <Badge variant="outline">{aiDraftResult.draft.model}</Badge>
                    </div>

                    <Separator />

                    <div>
                      <Label className="text-xs text-muted-foreground">System Instructions</Label>
                      <p className="text-xs bg-muted/50 p-2 rounded mt-1 max-h-24 overflow-y-auto">
                        {aiDraftResult.draft.template?.systemInstructions}
                      </p>
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">Style Guidance</Label>
                      <p className="text-xs bg-muted/50 p-2 rounded mt-1 max-h-24 overflow-y-auto">
                        {aiDraftResult.draft.template?.styleGuidance}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">Min Words</Label>
                        <p>{aiDraftResult.draft.template?.lengthBounds?.min}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Max Words</Label>
                        <p>{aiDraftResult.draft.template?.lengthBounds?.max}</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>

          <SheetFooter className="flex-col sm:flex-row gap-2 mt-6 border-t pt-4">
            <Button variant="outline" onClick={closeAiDraftDrawer} className="w-full sm:w-auto" data-testid="button-cancel-ai-draft">
              Cancel
            </Button>
            {aiDraftResult && aiDraftTab === "review" && (
              <Button 
                onClick={handleAiDraftSave}
                disabled={createMutation.isPending}
                className="w-full sm:w-auto"
                data-testid="button-save-ai-draft"
              >
                {createMutation.isPending ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />Save as Custom Prompt</>
                )}
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
